import { cva, VariantProps } from 'class-variance-authority';
import { PropsWithChildren, ReactNode, useMemo } from 'react';

import { cn } from '../../../libs/utils';
import { semanticColor } from '../../../theme/semanticColor';
import { hexToRgb } from '../../util';

const stickyBottomVariants = cva('flex w-full', {
  variants: {
    align: {
      horizontal: 'flex-row gap-normal-small',
      vertical: 'flex-col gap-tiny-medium'
    }
  },
  defaultVariants: {
    align: 'horizontal'
  }
});

interface StickyBottomWrapperProps extends PropsWithChildren {
  bgColor?: 'transparent' | string;
  supportText?: ReactNode;
  className?: string;
  edge?: 'fade' | 'divider';
}

interface StickyBottomProps extends VariantProps<typeof stickyBottomVariants>, StickyBottomWrapperProps {}

const _SupportedText = ({ children }: PropsWithChildren) => {
  return <div className={'caption-medium-regular mb-normal-small w-full text-center text-neutral-foreground-low'}>{children}</div>;
};

const BaseStickyBottomWrapper = ({ children, edge = 'fade', bgColor = '#FFF', supportText, align, className }: StickyBottomProps) => {
  const resolvedBgColor = bgColor === 'transparent' ? semanticColor.neutral.background.transparent : bgColor;

  const backgroundColor = useMemo(() => {
    const rgb = bgColor.includes('#') ? hexToRgb(bgColor) : bgColor;
    const rgbArray = bgColor === 'transparent' ? [] : rgb.match(/\d+/g) || [];
    const [red = 255, green = 255, blue = 255] = rgbArray;

    if (bgColor === 'transparent') {
      return '';
    } else if (edge === 'fade') {
      return `linear-gradient(180deg, rgba(${red}, ${green}, ${blue}, 0.00) 0%, rgba(${red}, ${green}, ${blue}, 0.75) 50%, rgba(${red}, ${green}, ${blue}, 1) 100%)`;
    } else if (edge === 'divider') {
      return bgColor;
    }
    return '';
  }, [bgColor, edge]);

  return (
    <div
      className={cn('relative z-sticky-button flex flex-col items-center justify-center pb-normal-medium', className)}
      style={{ backgroundColor: resolvedBgColor }}
    >
      <div
        className={cn('absolute left-0 right-0 top-negative-normal-medium h-normal-medium w-full', {
          'border-t border-t-neutral-border-high !h-tiny-large !-top-tiny-large': edge === 'divider'
        })}
        style={{
          background: backgroundColor
        }}
      />
      <div className={'container-padding-medium container-medium w-full'}>
        {supportText && <_SupportedText>{supportText}</_SupportedText>}
        <div className={cn(stickyBottomVariants({ align }))}>{children}</div>
      </div>
    </div>
  );
};

const StickyBottomWrapper = ({ className, ...props }: StickyBottomProps) => {
  return <BaseStickyBottomWrapper {...props} className={cn('fixed bottom-0 left-0 right-0', className)} />;
};

const ModalStickyBottomWrapper = (props: StickyBottomProps) => {
  return <BaseStickyBottomWrapper {...props} />;
};

export { StickyBottomWrapper, ModalStickyBottomWrapper };
